// hooks
import usePreviousValue from '@hooks/use-previous-value';
// utils
import { convertEnvironmentalFooterIconSVGToImage } from '@modules/email-signatures-helpers';
import { isEqual } from 'es-toolkit';
import { useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';

// consts and types
import { EM_SIG_CONTACT_ICON_SIZE } from '../consts';
// components
import EmailSignatureIcon from '../EmailSignatureIcon';
import type { EmailSignatureEnvironmentalFooterId, EmailSignatureV2 } from '../types';

/**
 * This hook will generate new icon for the environmental footer based on the .
 * @param emailSignature The email signature object
 * @param handleUpdateNewLocalEnvironmentalIconUrl The callback function used to update the button image
 * @param enable The flag indicates whether the hook should run
 */
function useEnvironmentalFooterIconGenerator(
  emailSignature: EmailSignatureV2,
  handleUpdateNewLocalEnvironmentalIconUrl: (
    iconImageUrl: string,
    environmentalFooterId: EmailSignatureEnvironmentalFooterId,
  ) => void,
  enable: boolean,
): void {
  const previousEmailSignature = usePreviousValue(
    emailSignature,
    (prevEmailSignature, currentEmailSignature) =>
      enable ? isEqual(prevEmailSignature, currentEmailSignature) : true,
  );

  useEffect(() => {
    if (!enable || !previousEmailSignature) return;

    const previousEmailSignatureEnvironmentalFooter = previousEmailSignature.environmentalFooter;
    const currentEmailSignatureEnvironmentalFooter = emailSignature.environmentalFooter;
    if (
      (previousEmailSignatureEnvironmentalFooter?.baseIcon ===
        currentEmailSignatureEnvironmentalFooter?.baseIcon &&
        previousEmailSignatureEnvironmentalFooter?.id ===
          currentEmailSignatureEnvironmentalFooter?.id) ||
      !currentEmailSignatureEnvironmentalFooter
    ) {
      return;
    }

    const environmentalFooterIconMarkup = ReactDOMServer.renderToStaticMarkup(
      <EmailSignatureIcon
        icon={currentEmailSignatureEnvironmentalFooter.baseIcon}
        inlineStyle={{
          width: `${EM_SIG_CONTACT_ICON_SIZE}px`,
          height: `${EM_SIG_CONTACT_ICON_SIZE}px`,
          color: '#3DB512',
        }}
      />,
    );

    const environmentalFooterNewIconImage = convertEnvironmentalFooterIconSVGToImage(
      environmentalFooterIconMarkup,
    );

    handleUpdateNewLocalEnvironmentalIconUrl(
      environmentalFooterNewIconImage ?? '',
      currentEmailSignatureEnvironmentalFooter.id,
    );
  }, [
    emailSignature.environmentalFooter,
    enable,
    handleUpdateNewLocalEnvironmentalIconUrl,
    previousEmailSignature,
  ]);
}

export default useEnvironmentalFooterIconGenerator;
